import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.js';

// ─── Brand icon ───────────────────────────────────────────────────────────────

function CommandFloorIcon({ size = 32 }: { size?: number }) {
  const half = size / 2;
  const r = size * 0.19;
  // Place centers so there is exactly 2px of space between adjacent octagons.
  // Each octagon's half-width = r * cos(π/8) ≈ 0.924r; gap = size - 2*offset - 2*halfW = 2.
  const offset = (size - 2 * r * Math.cos(Math.PI / 8) - 4) / 2;

  function oct(cx: number, cy: number, radius: number) {
    return Array.from({ length: 8 }, (_, i) => {
      const a = (i * Math.PI) / 4 + Math.PI / 8;
      return `${(cx + radius * Math.cos(a)).toFixed(2)},${(cy + radius * Math.sin(a)).toFixed(2)}`;
    }).join(' ');
  }

  const cx1 = offset;
  const cy1 = offset;
  const cx2 = size - offset;
  const cy2 = offset;
  const cx3 = offset;
  const cy3 = size - offset;
  const cx4 = size - offset;
  const cy4 = size - offset;

  const sw = 2;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ display: 'block', flexShrink: 0 }}
    >
      {/* upper-left: blue "next up" */}
      <polygon points={oct(cx1, cy1, r)} fill="#E2E8F0" stroke="#2563EB" strokeWidth={sw} />
      {/* upper-right: yellow / help needed */}
      <polygon points={oct(cx2, cy2, r)} fill="#FCD34D" stroke="#B45309" strokeWidth={sw} />
      {/* lower-left: red / stopped */}
      <polygon points={oct(cx3, cy3, r)} fill="#FCA5A5" stroke="#991B1B" strokeWidth={sw} />
      {/* lower-right: grey / on track */}
      <polygon points={oct(cx4, cy4, r)} fill="#E2E8F0" stroke="#64748B" strokeWidth={sw} />
    </svg>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function IconFloor() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="2" width="7" height="7" rx="1.5" />
      <rect x="11" y="2" width="7" height="7" rx="1.5" />
      <rect x="2" y="11" width="7" height="7" rx="1.5" />
      <rect x="11" y="11" width="7" height="7" rx="1.5" />
    </svg>
  );
}

function IconList() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    >
      <line x1="4" y1="6" x2="16" y2="6" />
      <line x1="4" y1="10" x2="16" y2="10" />
      <line x1="4" y1="14" x2="16" y2="14" />
    </svg>
  );
}

function IconQuestion() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="10" cy="10" r="8" />
      <path d="M7.5 7.5a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 3.5" />
      <circle cx="10" cy="14.5" r="0.75" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconChevronRight() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6,3 11,8 6,13" />
    </svg>
  );
}

function IconChevronLeft() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="10,3 5,8 10,13" />
    </svg>
  );
}

// Horizontal three-dots (⋯) per Pipedrive pattern
function IconDotsHorizontal() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
      <circle cx="4.5" cy="10" r="1.5" />
      <circle cx="10" cy="10" r="1.5" />
      <circle cx="15.5" cy="10" r="1.5" />
    </svg>
  );
}

function IconAdmin() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 2L3 5v5c0 4.418 3.134 7.72 7 8 3.866-.28 7-3.582 7-8V5l-7-3z" />
    </svg>
  );
}

function IconSettings() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="10" cy="10" r="3" />
      <path d="M10 1v2M10 17v2M1 10h2M17 10h2M3.22 3.22l1.42 1.42M15.36 15.36l1.42 1.42M3.22 16.78l1.42-1.42M15.36 4.64l1.42-1.42" />
    </svg>
  );
}

function IconSignOut() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M13 3h4v14h-4" />
      <polyline points="9,14 13,10 9,6" />
      <line x1="3" y1="10" x2="13" y2="10" />
    </svg>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function UserAvatar({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-pyek-navy text-xs font-semibold text-slate-200 ring-1 ring-aurora-strong">
      {initials}
    </div>
  );
}

function NavItem({
  to,
  icon,
  label,
  expanded,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  expanded: boolean;
}) {
  return (
    <Link
      to={to}
      title={!expanded ? label : undefined}
      className={`flex h-10 w-full items-center gap-3 rounded-lg px-2 text-slate-400 transition-colors hover:bg-white/5 hover:text-slate-200 ${expanded ? '' : 'justify-center'}`}
    >
      <span className="shrink-0">{icon}</span>
      {expanded && <span className="truncate text-sm">{label}</span>}
    </Link>
  );
}

function FlyoutItem({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex h-11 w-full items-center gap-4 px-4 text-sm transition-colors hover:bg-white/5 ${danger ? 'text-slate-400 hover:text-red-400' : 'text-slate-300 hover:text-slate-100'}`}
    >
      <span className="shrink-0 opacity-70">{icon}</span>
      {label}
    </button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function AppSidebar() {
  const [expanded, setExpanded] = useState(false);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const asideRef = useRef<HTMLElement>(null);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  // Close flyout on click outside the aside (which contains the flyout as a child)
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (flyoutOpen && asideRef.current && !asideRef.current.contains(e.target as Node)) {
        setFlyoutOpen(false);
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [flyoutOpen]);

  // ESC closes flyout
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setFlyoutOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  function go(path: string) {
    setFlyoutOpen(false);
    navigate(path);
  }

  const w = expanded ? 'w-52' : 'w-16';

  return (
    // z-20 ensures the sidebar (and its flyout child) renders above the floor SVG and
    // all page content, which are in the default stacking context (z-index: auto).
    <aside
      ref={asideRef}
      className={`relative z-20 flex ${w} flex-shrink-0 flex-col border-r border-aurora bg-surface-panel transition-[width] duration-200`}
      style={{ backdropFilter: 'blur(8px)' }}
    >
      {/* Logo */}
      <div className="flex h-14 shrink-0 items-center overflow-hidden px-3">
        <Link
          to="/"
          title="Command Floor"
          className={`flex items-center gap-2.5 ${expanded ? '' : 'justify-center w-full'}`}
        >
          <span className="shrink-0">
            <CommandFloorIcon size={32} />
          </span>
          {expanded && (
            <span className="truncate text-sm font-semibold tracking-tight text-slate-100">
              Command Floor
            </span>
          )}
        </Link>
      </div>

      {/* Primary nav */}
      <nav className="flex flex-1 flex-col gap-1 px-2 py-2">
        <NavItem to="/" icon={<IconFloor />} label="Floor View" expanded={expanded} />
        <NavItem to="/tables" icon={<IconList />} label="All Tables" expanded={expanded} />
        {user && (
          <button
            onClick={() => setFlyoutOpen((o) => !o)}
            title="More"
            aria-expanded={flyoutOpen}
            className={`flex h-10 w-full items-center gap-3 rounded-lg px-2 transition-colors ${
              flyoutOpen
                ? 'bg-white/10 text-slate-200'
                : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
            } ${expanded ? '' : 'justify-center'}`}
          >
            <span className="shrink-0">
              <IconDotsHorizontal />
            </span>
            {expanded && <span className="truncate text-sm">More</span>}
          </button>
        )}
      </nav>

      {/* Bottom strip: help only */}
      <div className="flex flex-col gap-1 px-2 pb-3">
        <button
          title="Help"
          className={`flex h-10 w-full items-center gap-3 rounded-lg px-2 text-slate-500 transition-colors hover:bg-white/5 hover:text-slate-300 ${expanded ? '' : 'justify-center'}`}
        >
          <span className="shrink-0">
            <IconQuestion />
          </span>
          {expanded && <span className="truncate text-sm">Help</span>}
        </button>
      </div>

      {/* Expand/collapse toggle — hidden while flyout is open to avoid overlap */}
      {!flyoutOpen && (
        <button
          onClick={() => setExpanded((e) => !e)}
          title={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          className="absolute -right-3 top-16 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-aurora-strong bg-surface-panel text-slate-400 shadow hover:text-slate-200"
        >
          {expanded ? <IconChevronLeft /> : <IconChevronRight />}
        </button>
      )}

      {/* ── Pipedrive-style flyout ────────────────────────────────────────────
          Positioned as a direct child of the aside so it's inside the z-20
          stacking context. left-full = right edge of the rail. h-full fills
          the sidebar height. pointer events work because the flyout DOM node
          is inside asideRef, so the click-outside check still works. */}
      {flyoutOpen && user && (
        <div
          className="flyout-in absolute left-full top-12 z-50 flex h-[calc(100%-3rem)] w-56 flex-col border-r border-aurora-strong bg-surface-panel shadow-2xl"
          style={{ backdropFilter: 'blur(12px)' }}
        >
          {/* Close button row */}
          <div className="flex h-9 flex-none items-center justify-end border-b border-aurora px-2">
            <button
              onClick={() => setFlyoutOpen(false)}
              title="Close"
              className="flex h-7 w-7 items-center justify-center rounded text-slate-500 hover:bg-white/5 hover:text-slate-300"
            >
              <IconChevronLeft />
            </button>
          </div>

          {/* Menu items */}
          <div className="flex flex-1 flex-col py-1">
            {(user.role === 'LEADERSHIP' || user.role === 'ENTITY_MANAGER') && (
              <FlyoutItem icon={<IconAdmin />} label="Admin" onClick={() => go('/admin')} />
            )}
            <FlyoutItem icon={<IconSettings />} label="Settings" onClick={() => go('/settings')} />
          </div>

          <div className="border-t border-aurora py-1">
            <FlyoutItem
              icon={<IconSignOut />}
              label="Sign out"
              onClick={() => void signOut()}
              danger
            />
          </div>
        </div>
      )}
    </aside>
  );
}
